import React, { useState } from 'react';
import { useOutlet } from '../../context/OutletContext';
import { useOrders } from '../context/OrderContext';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaBuilding, FaPlus, FaEdit, FaChartLine, FaCog, FaMapMarkerAlt, FaPhone, FaEnvelope, FaCopy, FaCheck } from 'react-icons/fa';
import './Outlets.css';

const Outlets = () => {
  const { outlets, selectedOutlet, selectOutlet } = useOutlet();
  const { getAllOutletsStats } = useOrders();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const outletStats = getAllOutletsStats();

  const copyOutletId = (outletId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(outletId);
    setCopiedId(outletId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAddOutlet = () => {
    setEditingOutlet(null);
    setShowAddModal(true);
  };

  const handleEditOutlet = (outlet) => {
    setEditingOutlet(outlet);
    setShowAddModal(true);
  };

  const handleSaveOutlet = async (outletData) => {
    try {
      if (editingOutlet) {
        // Update existing outlet
        await updateDoc(doc(db, 'outlets', editingOutlet.id), {
          ...outletData,
          updatedAt: Timestamp.now()
        });
        alert('Outlet updated successfully!');
      } else {
        // Add new outlet
        await addDoc(collection(db, 'outlets'), {
          ...outletData,
          isActive: true,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        alert('Outlet added successfully!');
      }
      setShowAddModal(false);
      setEditingOutlet(null);
    } catch (error) {
      console.error('Error saving outlet:', error);
      alert('Error saving outlet: ' + error.message);
    }
  };

  const getOutletStats = (outletId) => {
    return outletStats.find(stat => stat.outletId === outletId) || {
      totalOrders: 0,
      totalRevenue: 0,
      cancelled: 0
    };
  };

  return (
    <div className="outlets-page">
      <div className="page-header">
        <div className="header-left">
          <h1><FaBuilding /> Outlet Management</h1>
          <p>Manage multiple restaurant locations</p>
        </div>
        <button className="btn-primary" onClick={handleAddOutlet}>
          <FaPlus /> Add New Outlet
        </button>
      </div>

      <div className="outlets-grid">
        {outlets.map(outlet => {
          const stats = getOutletStats(outlet.id);
          const isSelected = selectedOutlet?.id === outlet.id;

          return (
            <div
              key={outlet.id}
              className={`outlet-card ${isSelected ? 'outlet-card--selected' : ''}`}
              onClick={() => selectOutlet(outlet.id)}
            >
              <div className="outlet-card__header">
                <div className="outlet-card__title">
                  <h2>{outlet.name}</h2>
                  {isSelected && <span className="badge badge-primary">Current</span>}
                </div>
                <div className="outlet-card__actions">
                  <button
                    className="btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOutlet(outlet);
                    }}
                    title="Edit outlet"
                  >
                    <FaEdit />
                  </button>
                </div>
              </div>

              <div className="outlet-card__id">
                <div className="outlet-id-container">
                  <span className="outlet-id-label">Outlet ID:</span>
                  <code className="outlet-id-value">{outlet.id}</code>
                  <button
                    className="btn-copy-id"
                    onClick={(e) => copyOutletId(outlet.id, e)}
                    title="Copy Outlet ID"
                  >
                    {copiedId === outlet.id ? <FaCheck /> : <FaCopy />}
                  </button>
                </div>
              </div>

              <div className="outlet-card__info">
                <div className="info-item">
                  <FaMapMarkerAlt className="info-icon" />
                  <span>{outlet.address}</span>
                </div>
                <div className="info-item">
                  <FaPhone className="info-icon" />
                  <span>{outlet.phone}</span>
                </div>
                <div className="info-item">
                  <FaEnvelope className="info-icon" />
                  <span>{outlet.email}</span>
                </div>
              </div>

              <div className="outlet-card__status">
                {outlet.settings?.shopOnline ? (
                  <span className="status-badge status-badge--open">Open</span>
                ) : (
                  <span className="status-badge status-badge--closed">Closed</span>
                )}
              </div>

              <div className="outlet-card__stats">
                <div className="stat-item">
                  <span className="stat-label">Total Orders</span>
                  <span className="stat-value">{stats.totalOrders}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Revenue</span>
                  <span className="stat-value">₹{stats.totalRevenue.toFixed(2)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Cancelled</span>
                  <span className="stat-value stat-value--danger">{stats.cancelled}</span>
                </div>
              </div>

              <div className="outlet-card__features">
                {outlet.features?.dineIn && <span className="feature-badge">Dine-In</span>}
                {outlet.features?.delivery && <span className="feature-badge">Delivery</span>}
                {outlet.features?.takeaway && <span className="feature-badge">Takeaway</span>}
                {outlet.features?.qrOrdering && <span className="feature-badge">QR Order</span>}
              </div>
            </div>
          );
        })}
      </div>

      {showAddModal && (
        <OutletFormModal
          outlet={editingOutlet}
          onClose={() => {
            setShowAddModal(false);
            setEditingOutlet(null);
          }}
          onSave={handleSaveOutlet}
        />
      )}
    </div>
  );
};

// Outlet Form Modal Component
const OutletFormModal = ({ outlet, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: outlet?.name || '',
    address: outlet?.address || '',
    phone: outlet?.phone || '',
    email: outlet?.email || '',
    location: {
      latitude: outlet?.location?.latitude || 0,
      longitude: outlet?.location?.longitude || 0
    },
    settings: {
      shopOnline: outlet?.settings?.shopOnline ?? true,
      deliveryCharge: outlet?.settings?.deliveryCharge || 50,
      minimumOrder: outlet?.settings?.minimumOrder || 200,
      freeDeliveryAbove: outlet?.settings?.freeDeliveryAbove || 500,
      gstPercent: outlet?.settings?.gstPercent || 5,
      packagingCharge: outlet?.settings?.packagingCharge || 10,
      operatingHours: outlet?.settings?.operatingHours || {
        monday: { open: '09:00', close: '22:00', closed: false },
        tuesday: { open: '09:00', close: '22:00', closed: false },
        wednesday: { open: '09:00', close: '22:00', closed: false },
        thursday: { open: '09:00', close: '22:00', closed: false },
        friday: { open: '09:00', close: '22:00', closed: false },
        saturday: { open: '09:00', close: '22:00', closed: false },
        sunday: { open: '09:00', close: '22:00', closed: false }
      }
    },
    features: {
      dineIn: outlet?.features?.dineIn ?? true,
      delivery: outlet?.features?.delivery ?? true,
      takeaway: outlet?.features?.takeaway ?? true,
      qrOrdering: outlet?.features?.qrOrdering ?? true
    }
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content outlet-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{outlet ? 'Edit Outlet' : 'Add New Outlet'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="outlet-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Outlet Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="e.g., Odisha Zaika - Bhubaneswar"
              />
            </div>

            <div className="form-group">
              <label>Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required
                placeholder="Full address"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  placeholder="Contact number"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.location.latitude}
                  onChange={(e) => handleNestedChange('location', 'latitude', parseFloat(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.location.longitude}
                  onChange={(e) => handleNestedChange('location', 'longitude', parseFloat(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Settings</h3>
            <div className="form-group form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.settings.shopOnline}
                  onChange={(e) => handleNestedChange('settings', 'shopOnline', e.target.checked)}
                />
                <span>Shop Online</span>
              </label>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Delivery Charge (₹)</label>
                <input
                  type="number"
                  value={formData.settings.deliveryCharge}
                  onChange={(e) => handleNestedChange('settings', 'deliveryCharge', parseFloat(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Minimum Order (₹)</label>
                <input
                  type="number"
                  value={formData.settings.minimumOrder}
                  onChange={(e) => handleNestedChange('settings', 'minimumOrder', parseFloat(e.target.value))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Free Delivery Above (₹)</label>
                <input
                  type="number"
                  value={formData.settings.freeDeliveryAbove}
                  onChange={(e) => handleNestedChange('settings', 'freeDeliveryAbove', parseFloat(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>GST (%)</label>
                <input
                  type="number"
                  value={formData.settings.gstPercent}
                  onChange={(e) => handleNestedChange('settings', 'gstPercent', parseFloat(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Operating Hours</h3>
            <div className="operating-hours-grid">
              {Object.entries(formData.settings.operatingHours).map(([day, hours]) => (
                <div key={day} className="operating-hours-row">
                  <div className="day-name-col">
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </div>

                  <label className="day-toggle-col">
                    <input
                      type="checkbox"
                      checked={!hours.closed}
                      onChange={(e) => {
                        const newHours = { ...formData.settings.operatingHours };
                        newHours[day] = { ...hours, closed: !e.target.checked };
                        handleNestedChange('settings', 'operatingHours', newHours);
                      }}
                    />
                    <span className="toggle-slider-mini"></span>
                  </label>

                  {!hours.closed ? (
                    <div className="time-inputs-col">
                      <input
                        type="time"
                        value={hours.open || '09:00'}
                        onChange={(e) => {
                          const newHours = { ...formData.settings.operatingHours };
                          newHours[day] = { ...hours, open: e.target.value };
                          handleNestedChange('settings', 'operatingHours', newHours);
                        }}
                        className="time-input-small"
                      />
                      <span className="time-separator">to</span>
                      <input
                        type="time"
                        value={hours.close || '22:00'}
                        onChange={(e) => {
                          const newHours = { ...formData.settings.operatingHours };
                          newHours[day] = { ...hours, close: e.target.value };
                          handleNestedChange('settings', 'operatingHours', newHours);
                        }}
                        className="time-input-small"
                      />
                    </div>
                  ) : (
                    <div className="time-inputs-col">
                      <span className="closed-text">Closed</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Features</h3>
            <div className="form-group form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.features.dineIn}
                  onChange={(e) => handleNestedChange('features', 'dineIn', e.target.checked)}
                />
                <span>Dine-In Service</span>
              </label>
            </div>

            <div className="form-group form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.features.delivery}
                  onChange={(e) => handleNestedChange('features', 'delivery', e.target.checked)}
                />
                <span>Delivery Service</span>
              </label>
            </div>

            <div className="form-group form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.features.takeaway}
                  onChange={(e) => handleNestedChange('features', 'takeaway', e.target.checked)}
                />
                <span>Takeaway Service</span>
              </label>
            </div>

            <div className="form-group form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.features.qrOrdering}
                  onChange={(e) => handleNestedChange('features', 'qrOrdering', e.target.checked)}
                />
                <span>QR Ordering</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {outlet ? 'Update Outlet' : 'Add Outlet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Outlets;
